<template>
  <vs-popup
    class="apple-modal modal-simulacao-regra"
    title="Simulador & Prévia de Elegíveis da Régua"
    style="color: white"
    :active.sync="activeLocal"
    @close="fechar"
  >
    <div class="apple-modal-body">
      <!-- Barra de Controle Minimalista: Data de Referência e Contadores -->
      <div class="apple-sim-toolbar mb-3">
        <!-- Linha 1: Data e Atualizar -->
        <div class="apple-toolbar-row-1">
          <div class="flex items-center gap-3 flex-wrap">
            <label class="apple-label m-0 text-slate-700 font-semibold">Data de Referência:</label>
            <input
              v-model="dataReferencia"
              type="date"
              class="apple-native-input apple-input-sm"
              @change="carregarSimulacao"
            />
            <button
              type="button"
              class="apple-dialog-btn apple-btn-cancel py-1.5 px-3 text-xs"
              :disabled="carregando"
              @click="carregarSimulacao"
            >
              <feather-icon icon="RefreshCwIcon" svgClasses="w-3.5 h-3.5 mr-1.5 inline" :class="{ 'animate-spin': carregando }" />
              <span>Atualizar</span>
            </button>
          </div>

          <div>
            <span class="apple-pill-badge-active">
              Total: {{ resultado ? resultado.totalGeral : 0 }} elegível(is)
            </span>
          </div>
        </div>

        <!-- Linha 2: Contadores de Seleção e Ações Rápidas -->
        <div v-if="resultado && resultado.totalGeral > 0" class="apple-toolbar-row-2">
          <div class="flex items-center gap-2">
            <span class="text-xs text-slate-500 font-semibold uppercase tracking-wider">Destinatários:</span>
            <span class="apple-pill-badge-selected">
              {{ idsSelecionados.length }} de {{ resultado.totalGeral }} selecionado(s)
            </span>
          </div>

          <div class="flex items-center gap-2">
            <button
              type="button"
              class="apple-btn-quick-select"
              title="Marcar todos os destinatários"
              @click="marcarTodos"
            >
              <feather-icon icon="CheckSquareIcon" svgClasses="w-3.5 h-3.5 mr-1.5 inline text-slate-500" />
              Marcar Todos
            </button>
            <button
              type="button"
              class="apple-btn-quick-select"
              title="Desmarcar todos"
              @click="desmarcarTodos"
            >
              <feather-icon icon="SquareIcon" svgClasses="w-3.5 h-3.5 mr-1.5 inline text-slate-500" />
              Desmarcar
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="carregando" class="apple-state-box py-10">
        <div class="apple-loader"></div>
        <p class="apple-state-text">Calculando critérios e buscando registros no banco...</p>
      </div>

      <!-- Empty State 1: Nenhuma regra configurada ou ativa no sistema -->
      <div v-else-if="!resultado || !resultado.regras || resultado.regras.length === 0 || (resultado.totalRegrasAvaliadas === 0)" class="apple-state-box py-10">
        <div class="apple-state-icon bg-amber-50 text-amber-600 border border-amber-200">
          <feather-icon icon="AlertCircleIcon" svgClasses="w-6 h-6 text-amber-500" />
        </div>
        <h4 class="apple-state-title">Nenhuma regra cadastrada ou ativa</h4>
        <p class="apple-state-text">
          Você ainda não possui regras de <strong>Pós-Venda</strong> ou <strong>Cobrança</strong> ativas no sistema.<br/>
          Crie uma regra primeiro para que o simulador possa identificar clientes e OSs elegíveis.
        </p>
      </div>

      <!-- Empty State 2: Regras existem, mas nenhum cliente elegível na data selecionada -->
      <div v-else-if="resultado.totalGeral === 0" class="apple-state-box py-10">
        <div class="apple-state-icon bg-slate-50 text-slate-500 border border-slate-200">
          <feather-icon icon="InboxIcon" svgClasses="w-6 h-6 text-slate-400" />
        </div>
        <h4 class="apple-state-title">Nenhum registro elegível para esta data</h4>
        <p class="apple-state-text">
          Foram avaliadas <strong>{{ resultado.totalRegrasAvaliadas || resultado.regras.length }} regra(s)</strong>, mas nenhuma OS ou Conta atende aos critérios de gatilho na data <strong>{{ formatarData(dataReferencia) }}</strong>.
        </p>
        <div class="text-xs text-slate-500 mt-2 bg-slate-50 py-1.5 px-3 rounded-full inline-block border border-slate-200">
          💡 Dica: Altere a <strong>Data de Referência</strong> acima para simular outras datas de execução ou vencimento.
        </div>
      </div>

      <!-- Lista de Resultados por Regra (Container com Scroll Dedicado) -->
      <div v-else class="apple-sim-list">
        <div
          v-for="itemRegra in resultado.regras"
          :key="itemRegra.regra.id"
          class="apple-rule-group-card mb-3"
        >
          <!-- Cabeçalho do Card da Regra -->
          <div class="apple-rule-group-header">
            <div>
              <div class="flex items-center gap-2">
                <span class="apple-rule-name">{{ itemRegra.regra.nome }}</span>
                <span class="apple-badge" :class="itemRegra.regra.tipo === 'pos_venda' ? 'badge-purple' : 'badge-green'">
                  {{ itemRegra.regra.tipo === 'pos_venda' ? 'Pós-Venda' : 'Cobrança' }}
                </span>
              </div>
              <div class="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                <span>Template: <strong>{{ itemRegra.regra.template_nome }}</strong></span>
                <span v-if="itemRegra.regra.tipo === 'pos_venda'" class="text-purple-700 font-medium">
                  {{ itemRegra.regra.sequencia_os ? itemRegra.regra.sequencia_os + 'ª OS' : 'Todas as OSs' }} ({{ itemRegra.regra.dias_apos_execucao }} dias após)
                </span>
                <span v-if="itemRegra.regra.tipo === 'cobranca'" class="text-amber-700 font-medium">
                  {{ itemRegra.regra.tipo_periodo === 'antes' ? itemRegra.regra.dias_relativo_vencimento + ' dias antes' : (itemRegra.regra.tipo_periodo === 'no_vencimento' ? 'No vencimento' : itemRegra.regra.dias_relativo_vencimento + ' dias após') }}
                  <span v-if="itemRegra.regra.ids_formas_pagamento" class="ml-1 text-slate-600 font-normal">
                    ({{ itemRegra.regra.ids_formas_pagamento.split(',').length }} forma(s) selecionada(s))
                  </span>
                  <span v-else class="ml-1 text-slate-600 font-normal">
                    (Todas as Formas)
                  </span>
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <span class="apple-badge badge-green font-semibold" style="font-size: 11.5px; padding: 3px 9px;">
                {{ itemRegra.total }} elegível(is)
              </span>
            </div>
          </div>

          <!-- Tabela de Itens da Regra -->
          <div v-if="itemRegra.itens && itemRegra.itens.length > 0" class="apple-table-container mt-2.5">
            <table class="apple-clean-table">
              <thead>
                <tr>
                  <th style="width: 10%; text-align: center;">
                    <div class="flex items-center justify-center">
                      <input
                        type="checkbox"
                        class="apple-checkbox-custom"
                        :checked="todosDaRegraSelecionados(itemRegra)"
                        :indeterminate.prop="algunsDaRegraSelecionados(itemRegra)"
                        title="Selecionar / Desmarcar todos desta regra"
                        @change="toggleTodosDaRegra(itemRegra)"
                      />
                    </div>
                  </th>
                  <th style="width: 10%;">ID</th>
                  <th style="width: 27%;">Cliente</th>
                  <th style="width: 22%;">WhatsApp / Contato</th>
                  <th v-if="itemRegra.regra.tipo === 'pos_venda'" style="width: 19%;">Serviço / Execução</th>
                  <th v-if="itemRegra.regra.tipo === 'pos_venda'" style="width: 12%;">Sequência</th>
                  <th v-if="itemRegra.regra.tipo === 'cobranca'" style="width: 15%;">Vencimento</th>
                  <th v-if="itemRegra.regra.tipo === 'cobranca'" style="width: 12%;">Valor</th>
                  <th v-if="itemRegra.regra.tipo === 'cobranca'" style="width: 14%;">Forma</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="item in obterItensPaginados(itemRegra)" 
                  :key="obterItemId(item)" 
                  class="apple-table-line"
                  :class="{ 'apple-row-selected': estaSelecionado(item) }"
                >
                  <td style="text-align: center;">
                    <div class="apple-select-cell">
                      <input
                        type="checkbox"
                        class="apple-checkbox-custom"
                        :checked="estaSelecionado(item)"
                        title="Selecionar para disparo"
                        @change="toggleItem(item)"
                      />
                      <button
                        type="button"
                        class="apple-btn-radio-single"
                        :class="{ 'apple-btn-radio-active': estaSelecionado(item) && idsSelecionados.length === 1 }"
                        :title="'Selecionar apenas este item (#' + obterItemId(item) + ')'"
                        @click="selecionarApenasUm(item)"
                      >
                        Só este
                      </button>
                    </div>
                  </td>
                  <td class="apple-item-id">#{{ obterItemId(item) }}</td>
                  <td>
                    <div class="apple-title-line">{{ item.nome_cliente || item.razao_social || '-' }}</div>
                    <div class="apple-meta-line">{{ item.cpf_cnpj || '' }}</div>
                  </td>
                  <td>
                    <div class="apple-phone-cell">
                      <feather-icon icon="MessageCircleIcon" svgClasses="w-4 h-4 shrink-0" :class="item.telefone ? 'text-emerald-600' : 'text-rose-500'" />
                      <span :class="item.telefone ? 'apple-phone-text' : 'apple-phone-empty'">
                        {{ item.telefone || 'Sem telefone padrão' }}
                      </span>
                    </div>
                  </td>
                  <!-- Pós-Venda -->
                  <td v-if="itemRegra.regra.tipo === 'pos_venda'">
                    <div class="apple-title-line">{{ item.servico_nome || 'Serviço Concluído' }}</div>
                    <div class="apple-meta-line font-medium">Exec: {{ item.data_execucao_formatada || item.data_execucao }}</div>
                  </td>
                  <td v-if="itemRegra.regra.tipo === 'pos_venda'">
                    <span class="apple-badge badge-purple">
                      {{ item.sequencia_os_calculada }}ª OS
                    </span>
                  </td>
                  <!-- Cobrança -->
                  <td v-if="itemRegra.regra.tipo === 'cobranca'">
                    <div class="apple-date-text">{{ item.data_vencimento_formatada || item.data_vencimento }}</div>
                    <div :class="item.dias_atraso > 0 ? 'text-rose-600 font-semibold' : 'text-blue-600 font-medium'" style="font-size: 11px;">
                      {{ item.dias_atraso > 0 ? item.dias_atraso + ' dias de atraso' : (item.dias_atraso === 0 ? 'Vence hoje' : Math.abs(item.dias_atraso) + ' dias a vencer') }}
                    </div>
                  </td>
                  <td v-if="itemRegra.regra.tipo === 'cobranca'">
                    <span class="apple-val-text">{{ formatarMoeda(item.valor_aberto || item.valor) }}</span>
                  </td>
                  <td v-if="itemRegra.regra.tipo === 'cobranca'">
                    <FormaPagamentoBadge :forma="item" size="12" />
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Barra de Paginação da Tabela -->
            <div v-if="itemRegra.itens.length > porPagina" class="apple-pagination-bar">
              <div class="apple-pagination-info">
                Mostrando <strong>{{ ((obterPaginaAtual(itemRegra.regra.id) - 1) * porPagina) + 1 }}</strong> a <strong>{{ Math.min(obterPaginaAtual(itemRegra.regra.id) * porPagina, itemRegra.itens.length) }}</strong> de <strong>{{ itemRegra.itens.length }}</strong> registros
              </div>
              <div class="apple-pagination-controls">
                <button
                  type="button"
                  class="apple-page-btn"
                  :disabled="obterPaginaAtual(itemRegra.regra.id) === 1"
                  title="Primeira Página"
                  @click="mudarPagina(itemRegra.regra.id, 1)"
                >
                  «
                </button>
                <button
                  type="button"
                  class="apple-page-btn"
                  :disabled="obterPaginaAtual(itemRegra.regra.id) === 1"
                  title="Página Anterior"
                  @click="mudarPagina(itemRegra.regra.id, obterPaginaAtual(itemRegra.regra.id) - 1)"
                >
                  ‹ Anterior
                </button>
                <span class="apple-page-indicator">
                  Pág. {{ obterPaginaAtual(itemRegra.regra.id) }} / {{ obterTotalPaginas(itemRegra) }}
                </span>
                <button
                  type="button"
                  class="apple-page-btn"
                  :disabled="obterPaginaAtual(itemRegra.regra.id) >= obterTotalPaginas(itemRegra)"
                  title="Próxima Página"
                  @click="mudarPagina(itemRegra.regra.id, obterPaginaAtual(itemRegra.regra.id) + 1)"
                >
                  Próxima ›
                </button>
                <button
                  type="button"
                  class="apple-page-btn"
                  :disabled="obterPaginaAtual(itemRegra.regra.id) >= obterTotalPaginas(itemRegra)"
                  title="Última Página"
                  @click="mudarPagina(itemRegra.regra.id, obterTotalPaginas(itemRegra))"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rodapé Fixo do Modal: Botões Sempre Visíveis -->
      <div class="apple-dialog-footer">
        <button type="button" class="apple-dialog-btn apple-btn-cancel" @click="fechar">
          Fechar
        </button>
        <button
          type="button"
          class="apple-dialog-btn apple-btn-save"
          :disabled="!resultado || resultado.totalGeral === 0 || idsSelecionados.length === 0 || disparando"
          @click="executarDisparo"
        >
          <feather-icon icon="SendIcon" svgClasses="w-3.5 h-3.5 mr-1.5 inline" />
          <span>{{ obterTextoBotaoDisparo() }}</span>
        </button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: "ModalSimulacaoRegra",
  props: {
    active: {
      type: Boolean,
      default: false
    },
    idRegra: {
      type: [Number, String],
      default: null
    },
    tipo: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      activeLocal: false,
      carregando: false,
      disparando: false,
      dataReferencia: new Date().toISOString().split('T')[0],
      resultado: null,
      idsSelecionados: [],
      porPagina: 10,
      paginaAtualPorRegra: {}
    };
  },
  watch: {
    active(val) {
      this.activeLocal = val;
      if (val) {
        this.dataReferencia = new Date().toISOString().split('T')[0];
        this.carregarSimulacao();
      }
    },
    activeLocal(val) {
      this.$emit('update:active', val);
    }
  },
  methods: {
    fechar() {
      this.activeLocal = false;
      this.$emit('update:active', false);
    },
    formatarData(dt) {
      if (!dt) return '-';
      const parts = dt.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dt;
    },
    formatarMoeda(val) {
      if (val === null || val === undefined) return 'R$ 0,00';
      return Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    obterItemId(item) {
      if (!item) return null;
      return item.id_conta || item.id_os || item.id;
    },
    obterPaginaAtual(regraId) {
      return this.paginaAtualPorRegra[regraId] || 1;
    },
    obterTotalPaginas(itemRegra) {
      if (!itemRegra || !itemRegra.itens) return 1;
      return Math.ceil(itemRegra.itens.length / this.porPagina) || 1;
    },
    mudarPagina(regraId, novaPagina) {
      this.$set(this.paginaAtualPorRegra, regraId, Math.max(1, novaPagina));
    },
    obterItensPaginados(itemRegra) {
      if (!itemRegra || !itemRegra.itens) return [];
      const pagina = this.obterPaginaAtual(itemRegra.regra.id);
      const inicio = (pagina - 1) * this.porPagina;
      return itemRegra.itens.slice(inicio, inicio + this.porPagina);
    },
    estaSelecionado(item) {
      const id = typeof item === 'object' ? this.obterItemId(item) : item;
      return this.idsSelecionados.indexOf(id) !== -1;
    },
    toggleItem(item) {
      const id = typeof item === 'object' ? this.obterItemId(item) : item;
      const idx = this.idsSelecionados.indexOf(id);
      if (idx !== -1) {
        this.idsSelecionados.splice(idx, 1);
      } else {
        this.idsSelecionados.push(id);
      }
    },
    selecionarApenasUm(item) {
      const id = typeof item === 'object' ? this.obterItemId(item) : item;
      this.idsSelecionados = [id];
    },
    todosDaRegraSelecionados(itemRegra) {
      if (!itemRegra || !itemRegra.itens || itemRegra.itens.length === 0) return false;
      return itemRegra.itens.every(it => this.estaSelecionado(it));
    },
    algunsDaRegraSelecionados(itemRegra) {
      if (!itemRegra || !itemRegra.itens || itemRegra.itens.length === 0) return false;
      const count = itemRegra.itens.filter(it => this.estaSelecionado(it)).length;
      return count > 0 && count < itemRegra.itens.length;
    },
    toggleTodosDaRegra(itemRegra) {
      if (!itemRegra || !itemRegra.itens) return;
      const todosEstao = this.todosDaRegraSelecionados(itemRegra);
      const idsDaRegra = itemRegra.itens.map(it => this.obterItemId(it));
      if (todosEstao) {
        this.idsSelecionados = this.idsSelecionados.filter(id => idsDaRegra.indexOf(id) === -1);
      } else {
        idsDaRegra.forEach(id => {
          if (id != null && this.idsSelecionados.indexOf(id) === -1) {
            this.idsSelecionados.push(id);
          }
        });
      }
    },
    marcarTodos() {
      if (!this.resultado || !this.resultado.regras) return;
      const todos = [];
      this.resultado.regras.forEach(reg => {
        if (reg.itens) {
          reg.itens.forEach(it => {
            const id = this.obterItemId(it);
            if (id != null) todos.push(id);
          });
        }
      });
      this.idsSelecionados = todos;
    },
    desmarcarTodos() {
      this.idsSelecionados = [];
    },
    obterTextoBotaoDisparo() {
      if (this.disparando) return 'Iniciando Disparos...';
      if (!this.resultado || this.resultado.totalGeral === 0 || this.idsSelecionados.length === 0) {
        return 'Selecione destinatários';
      }
      if (this.idsSelecionados.length === this.resultado.totalGeral) {
        return `Disparar Lote Completo (${this.resultado.totalGeral} Elegíveis)`;
      }
      return `Disparar para ${this.idsSelecionados.length} Selecionado(s)`;
    },
    async carregarSimulacao() {
      this.carregando = true;
      try {
        const payload = {
          dataReferencia: this.dataReferencia
        };
        if (this.idRegra) {
          payload.idRegra = this.idRegra;
        }
        if (this.tipo) {
          payload.tipo = this.tipo;
        }

        const res = await this.$http.post('regua-disparos/simular', payload);
        this.resultado = res && res.data ? res.data : res;
        this.paginaAtualPorRegra = {};
        this.marcarTodos();
      } catch (err) {
        const error = this.$httpErrors ? this.$httpErrors(err) : { text: err.message };
        this.$vs.notify({
          title: "Erro ao simular",
          text: error.text || err.message,
          color: "danger"
        });
      } finally {
        this.carregando = false;
      }
    },
    async executarDisparo() {
      if (!this.resultado || this.resultado.totalGeral === 0 || this.idsSelecionados.length === 0) return;

      this.disparando = true;
      try {
        const payload = {
          dataReferencia: this.dataReferencia,
          idsSelecionados: this.idsSelecionados
        };
        if (this.idRegra) {
          payload.idRegra = this.idRegra;
        }
        if (this.tipo) {
          payload.tipo = this.tipo;
        }

        const res = await this.$http.post('regua-disparos/executar', payload);
        this.$vs.notify({
          title: "Disparo Iniciado",
          text: `O processamento da régua foi iniciado para ${this.idsSelecionados.length} destinatário(s) no WATI!`,
          color: "success"
        });
        this.$emit('disparado', res);
        this.fechar();
      } catch (err) {
        const error = this.$httpErrors ? this.$httpErrors(err) : { text: err.message };
        this.$vs.notify({
          title: "Erro ao executar disparo",
          text: error.text || err.message,
          color: "danger"
        });
      } finally {
        this.disparando = false;
      }
    }
  }
};
</script>

<style scoped>
/* =========================================================
   Modal Dialog Styling (Padrão Apple / SCORPIO ERP)
========================================================= */
.apple-modal-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(88vh - 85px);
  overflow: hidden;
  padding: 8px 12px 0;
}

.apple-sim-toolbar {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  flex-shrink: 0;
}

.apple-toolbar-row-1 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
}

.apple-toolbar-row-2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 10px;
}

.apple-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #0f172a;
}

.apple-native-input {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  padding: 0 10px;
  font-size: 13px;
  color: #0f172a;
  outline: none;
  transition: all 0.18s ease;
}

.apple-native-input:focus {
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
}

.apple-pill-badge-active {
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 100px;
  background: linear-gradient(180deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(2, 132, 199, 0.25);
  white-space: nowrap;
}

.apple-pill-badge-selected {
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 100px;
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  white-space: nowrap;
}

.apple-btn-quick-select {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  white-space: nowrap;
}

.apple-btn-quick-select:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #94a3b8;
}

.apple-sim-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px;
  margin-bottom: 6px;
}

.apple-rule-group-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.apple-rule-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.apple-rule-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.apple-select-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.apple-checkbox-custom {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #0284c7;
  margin: 0;
  flex-shrink: 0;
}

.apple-btn-radio-single {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  line-height: 1.2;
}

.apple-btn-radio-single:hover,
.apple-btn-radio-single.apple-btn-radio-active {
  background: #0284c7;
  color: #ffffff;
  border-color: #0284c7;
}

.apple-phone-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.apple-row-selected {
  background: #f0f9ff !important;
}

.apple-row-selected td {
  background: #f0f9ff !important;
  color: #0f172a !important;
}

.apple-clean-table tbody tr td {
  color: #0f172a !important;
}

/* Paginação */
.apple-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.apple-pagination-info {
  font-size: 11.5px;
  color: #64748b;
}

.apple-pagination-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.apple-page-btn {
  height: 28px;
  padding: 0 10px;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.apple-page-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
}

.apple-page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.apple-page-indicator {
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  padding: 0 4px;
}

.apple-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
  flex-shrink: 0;
}

.apple-dialog-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  border-radius: 9px;
  padding: 0 20px;
  height: 38px;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  border: none;
  outline: none;
}

.apple-btn-save {
  background: linear-gradient(180deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(2, 132, 199, 0.25);
}

.apple-btn-save:hover {
  background: linear-gradient(180deg, #0369a1 0%, #075985 100%);
  box-shadow: 0 4px 10px rgba(2, 132, 199, 0.32);
  transform: translateY(-1px);
}

.apple-btn-save:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.apple-btn-cancel {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.apple-btn-cancel:hover {
  background: #f8fafc;
  color: #0f172a;
  border-color: #94a3b8;
}

/* Badges */
.apple-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
}

.badge-purple {
  background: #f5f3ff;
  color: #6d28d9;
}

.badge-green {
  background: #ecfdf5;
  color: #047857;
}

.badge-neutral {
  background: #f1f5f9;
  color: #475569;
}

/* Tabela */
.apple-table-container {
  overflow-x: auto;
}

.apple-clean-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.apple-clean-table th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.04em !important;
  color: #334155 !important;
  background: #f8fafc !important;
  padding: 10px 12px !important;
  border-bottom: 1px solid #cbd5e1 !important;
  text-align: left;
}

.apple-clean-table td {
  padding: 12px 10px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  color: #0f172a !important;
  vertical-align: middle;
}

.apple-table-line:last-child td {
  border-bottom: none;
}

.apple-item-id {
  font-family: monospace;
  font-size: 12px;
  font-weight: 700 !important;
  color: #334155 !important;
}

.apple-title-line {
  font-size: 12.5px;
  font-weight: 700 !important;
  color: #0f172a !important;
}

.apple-meta-line {
  font-size: 11px;
  font-weight: 500;
  color: #475569 !important;
}

.apple-phone-text {
  font-size: 12px;
  font-weight: 600 !important;
  color: #0f172a !important;
}

.apple-phone-empty {
  font-size: 11.5px;
  font-style: italic;
  color: #e11d48 !important;
}

.apple-date-text {
  font-size: 12.5px;
  font-weight: 700 !important;
  color: #0f172a !important;
}

.apple-val-text {
  font-size: 13px;
  font-weight: 700 !important;
  color: #0f172a !important;
}

/* Loading & Empty States */
.apple-state-box {
  text-align: center;
}

.apple-loader {
  width: 26px;
  height: 26px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #0071e3;
  border-radius: 50%;
  animation: appleSpin 0.7s infinite linear;
  margin: 0 auto 10px;
}

@keyframes appleSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.apple-state-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
}

.apple-state-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 2px;
}

.apple-state-text {
  font-size: 12.5px;
  color: #64748b;
  margin: 0;
}
</style>

<style>
/* =========================================================
   Estilos Globais do Modal de Simulacao (Vuesax Popup)
========================================================= */
.modal-simulacao-regra .vs-popup {
  max-width: 1060px !important;
  width: 95% !important;
  max-height: 88vh !important;
  display: flex !important;
  flex-direction: column !important;
  border-radius: 18px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
  overflow: hidden !important;
}

.modal-simulacao-regra .vs-popup--header,
.modal-simulacao-regra .vs-popup--header .vs-popup--title {
  padding: 16px 22px !important;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border-bottom: 1px solid #f1f5f9 !important;
  border-radius: 18px 18px 0 0 !important;
  flex-shrink: 0 !important;
}

.modal-simulacao-regra .vs-popup--title h3,
.modal-simulacao-regra .vs-popup--header h3,
.modal-simulacao-regra .vs-popup--header .vs-popup--title h3 {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #0f172a !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  letter-spacing: -0.01em !important;
}

.modal-simulacao-regra .vs-popup--close {
  background: #f1f5f9 !important;
  color: #64748b !important;
  border-radius: 8px !important;
  padding: 4px !important;
  top: 14px !important;
  right: 18px !important;
  transition: all 0.15s ease !important;
}

.modal-simulacao-regra .vs-popup--close:hover {
  background: #fee2e2 !important;
  color: #ef4444 !important;
}

.modal-simulacao-regra .vs-popup--content {
  padding: 16px 20px 16px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  flex: 1 !important;
  max-height: calc(88vh - 55px) !important;
}
</style>
